<script setup lang="ts">
import { onMounted, reactive, computed, watch, nextTick, ref, onUnmounted } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { TabsContent, TabsList, TabsRoot, TabsTrigger } from 'reka-ui';
import Textarea from '@/components/ui/TextArea.vue';
import Button from '@/components/ui/UIButton.vue';
import { ArrowLeft, Brain, Edit, GitCompare, Plus, Search as SearchIcon, Trash, Undo2, X, ArrowUp } from '@lucide/vue';
import { aiService } from '@/services/AIService';
import { referenceService } from '@/services/api';
import type { Prompt, PromptListRow } from '@/types/ui/PromptManager';
import type { AIModel, AIPromptMapping, CreatePromptRequest, UpdatePromptRequest, PromptDesignation } from '@/types/ai';
import type { LabelValue } from '@/types/label-value';
import { buildPromptListRows } from '@/utilities/promptListRows';
import Input from '@/components/ui/UIInput.vue';
import UISelect from '@/components/ui/UISelect.vue';
import AppModal from '@/components/v2/AppModal.vue';
import AppSelect from '@/components/v2/AppSelect.vue';
import RadioGroup from '@/components/v2/RadioGroup.vue';
import Badge from '@/components/ui/Badge.vue';
import JsonSchemaBuilder from '@/components/prompt/JsonSchemaBuilder.vue';
import PromptEntityContextPanel from '@/components/prompt/PromptEntityContextPanel.vue';
import PromptListTable from '@/components/prompt/PromptListTable.vue';
import PublishDiffModal from '@/components/prompt/PublishDiffModal.vue';
import SavedStatus, { type SavedStatusState } from '@/components/prompt/SavedStatus.vue';
import { normalizeJson } from '@/utilities/normalizeJson';
import { StoreMutationType } from '@/types/StoreMutationType.js';
import { Commit } from '@/store/index.js';

const route = useRoute();
const router = useRouter();

// -----
// interfaces
// -----

interface DeleteModal {
  title: string;
  open: boolean;
  actionLabel: string;
}

// -----
// reactive state
// -----

// Core prompt data
const promptState = reactive({
  prompts: [] as Prompt[],
  selectedPrompt: null as Prompt | null,
  newPrompts: [] as Prompt[],
});

// Current editing content
const contentState = reactive({
  currentPromptContent: '',
  currentSystemText: '',
  currentExpectedOutput: '',
});

// Validation error count emitted by JsonSchemaBuilder for the current expected-output schema
const outputFormatErrorCount = ref(0);

// Entity-context flags being edited, plus the last-saved values for dirty
// tracking. Flags persist on the output schema row server-side.
const entityContextState = reactive({
  carrier: false,
  coverageType: false,
  wholesaler: false,
});
const originalEntityContext = reactive({
  carrier: false,
  coverageType: false,
  wholesaler: false,
});

const designationOptions = [
  { label: 'Internal', value: 'internal' },
  { label: 'External policy', value: 'external' },
];

// UI state
const uiState = reactive({
  loading: false,
  loadingMessage: 'Saving changes...',
  currentTab: 'output-format',
  editingTitle: false,
  editingDescription: false,
});

// Form state
const formState = reactive({
  editedTitle: '',
  editedDescription: '',
  originalTitle: '',
  originalDescription: '',
});

// Dropdown selections
const selectionState = reactive({
  selectedPromptName: '',
  selectedVersion: '',
  availableVersions: [] as Prompt[],
});

// Reference data for the list view's Coverage Types column
const promptMappings = ref<AIPromptMapping[]>([]);
const coverageTypes = ref<LabelValue[]>([]);

// List filters live here rather than in the table: their controls sit beside
// the page title, the way the Mapping tab lays its header out.
const listSearch = ref('');
const listCoverageFilter = ref<string | null>(''); // AppSelect's clear emits null

// Modal state
const deleteModal = reactive<DeleteModal>({
  title: '',
  open: false,
  actionLabel: '',
});

// Scroll navigation state
const showScrollToTop = ref(false);

// Save status state
const lastSaveError = ref(false);
const justSavedAt = ref<number | null>(null);
const relativeTick = ref(0);
let relativeTickInterval: ReturnType<typeof setInterval> | null = null;
const JUST_SAVED_WINDOW_MS = 3000;

// Model selection state
const availableModels = ref<AIModel[]>([]);
const selectedModelArn = ref('');
const modelFieldError = ref(false);

// Validation regex for name: alphanumeric, underscore, hyphen, 1-100 characters
const nameRegex = /^[0-9a-zA-Z_-]{1,100}$/;

// ---------
// computed
// ---------
const isReadonly = computed(() => {
  return promptState.selectedPrompt?.version.toLowerCase() !== 'draft';
});

const isNewPrompt = computed(() => {
  return promptState.selectedPrompt?.promptId ? startsWith(promptState.selectedPrompt.promptId, 'new-') : false;
});

// Group prompts by ID for dropdown display, using draft name as the display name
const groupedPrompts = computed(() => {
  const groups = new Map<string, Prompt[]>();

  promptState.prompts.forEach((prompt) => {
    const promptId = String(prompt.promptId) || 'unknown';
    if (!groups.has(promptId)) {
      groups.set(promptId, []);
    }
    groups.get(promptId)!.push(prompt);
  });

  // Sort each group's versions (draft first, then by version number descending)
  groups.forEach((versions) => {
    versions.sort((a, b) => {
      const aIsDraft = a.version.toLowerCase() === 'draft';
      const bIsDraft = b.version.toLowerCase() === 'draft';

      if (aIsDraft && !bIsDraft) return -1;
      if (!aIsDraft && bIsDraft) return 1;

      if (aIsDraft && bIsDraft) {
        return new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime();
      }

      const aVersion = parseInt(a.version) || 0;
      const bVersion = parseInt(b.version) || 0;
      return bVersion - aVersion;
    });
  });

  // Create a map with display names (from draft) as keys and prompt groups as values
  const displayGroups = new Map<string, Prompt[]>();

  groups.forEach((versions) => {
    // Find the draft version to get the canonical name
    const draftVersion = versions.find((p) => p.version.toLowerCase() === 'draft');
    const displayName = draftVersion?.name || versions[0]?.name || 'Unnamed';

    displayGroups.set(displayName, versions);
  });

  // Sort by display name alphabetically
  return new Map([...displayGroups.entries()].sort(([a], [b]) => a.localeCompare(b)));
});

// The list is the landing state; a ?prompt= param opens the editor for that prompt.
const showList = computed(() => !route.query.prompt);

const promptListRows = computed<PromptListRow[]>(() =>
  buildPromptListRows(promptState.prompts, promptMappings.value, coverageTypes.value, availableModels.value),
);

const coverageFilterOptions = computed(() => {
  const names = new Set<string>();
  for (const row of promptListRows.value) {
    for (const name of row.coverageTypes) {
      names.add(name);
    }
  }
  return [...names].sort((a, b) => a.localeCompare(b)).map((name) => ({ value: name, label: name }));
});

const openPromptFromList = (promptKey: string, version: string) => {
  router.push({ query: { ...route.query, prompt: promptKey, version } });
};

const backToList = () => {
  const query = { ...route.query };
  delete query.prompt;
  delete query.version;
  router.push({ query });
  promptState.selectedPrompt = null;
  selectionState.selectedPromptName = '';
  selectionState.selectedVersion = '';
};

const titleError = computed(() => {
  if (!uiState.editingTitle || !formState.editedTitle) return '';
  if (!formState.editedTitle.trim()) {
    return 'Name is required';
  }
  if (!nameRegex.test(formState.editedTitle.trim())) {
    return 'Name must contain only alphanumeric characters, underscores, and hyphens (1-100 characters)';
  }
  return '';
});

const schemaStringsEqual = (a: string, b: string): boolean => {
  if (!a && !b) return true;
  if (!a || !b) return false;
  try {
    return JSON.stringify(normalizeJson(JSON.parse(a))) === JSON.stringify(normalizeJson(JSON.parse(b)));
  } catch {
    return a === b;
  }
};

const hasEntityContextChanges = computed(() => {
  if (!promptState.selectedPrompt) return false;
  return (
    entityContextState.carrier !== originalEntityContext.carrier ||
    entityContextState.coverageType !== originalEntityContext.coverageType ||
    entityContextState.wholesaler !== originalEntityContext.wholesaler
  );
});

// Tab-specific unsaved changes. The entity-context flags live on the Output
// Format tab, so their dirtiness rolls into it.
const hasOutputFormatChanges = computed(() => {
  if (!promptState.selectedPrompt) return false;
  return (
    !schemaStringsEqual(contentState.currentExpectedOutput || '', promptState.selectedPrompt.expectedOutputSchema || '') ||
    hasEntityContextChanges.value
  );
});

const hasSystemInstructionsChanges = computed(() => {
  if (!promptState.selectedPrompt) return false;
  return contentState.currentSystemText !== (promptState.selectedPrompt.systemPrompt || '');
});

const hasPromptTextChanges = computed(() => {
  if (!promptState.selectedPrompt) return false;
  return contentState.currentPromptContent !== (promptState.selectedPrompt.userPrompt || '');
});

const hasModelChanges = computed(() => {
  if (!promptState.selectedPrompt) return false;
  return selectedModelArn.value !== (promptState.selectedPrompt.modelArn || '');
});

// Global unsaved changes - combines all tab changes and metadata changes
const hasUnsavedChanges = computed(() => {
  if (!promptState.selectedPrompt) return false;

  return (
    hasOutputFormatChanges.value ||
    hasSystemInstructionsChanges.value ||
    hasPromptTextChanges.value ||
    hasModelChanges.value ||
    (promptState.selectedPrompt.name || '') !== formState.originalTitle ||
    (promptState.selectedPrompt.description || '') !== formState.originalDescription
  );
});

// The currently published version for this promptId — i.e. the latest non-draft
// version. Undefined if there is none yet.
const publishedPrompt = computed<Prompt | undefined>(() => {
  const selected = promptState.selectedPrompt;
  if (!selected) return undefined;
  let latest: Prompt | undefined;
  let latestVersion = -Infinity;
  for (const p of promptState.prompts) {
    if (p.promptId !== selected.promptId) continue;
    if (p.version.toLowerCase() === 'draft') continue;
    const v = parseInt(p.version, 10);
    if (Number.isFinite(v) && v > latestVersion) {
      latest = p;
      latestVersion = v;
    }
  }
  return latest;
});

const schemaDiffersFromPublished = computed(() => {
  if (!publishedPrompt.value) return false;
  return !schemaStringsEqual(contentState.currentExpectedOutput || '', publishedPrompt.value.expectedOutputSchema || '');
});

const systemDiffersFromPublished = computed(() => {
  if (!publishedPrompt.value) return false;
  return contentState.currentSystemText !== (publishedPrompt.value.systemPrompt || '');
});

const userPromptDiffersFromPublished = computed(() => {
  if (!publishedPrompt.value) return false;
  return contentState.currentPromptContent !== (publishedPrompt.value.userPrompt || '');
});

const hasChangesVsPublished = computed(
  () => !!publishedPrompt.value && (schemaDiffersFromPublished.value || systemDiffersFromPublished.value || userPromptDiffersFromPublished.value),
);

const showDiffModal = ref(false);

// Go's zero-value time.Time serializes as "0001-01-01T00:00:00Z". Treat it as missing.
const hasRealUpdatedAt = (value: string | undefined): boolean => {
  return !!value && !value.startsWith('0001-');
};

const savedStatus = computed<SavedStatusState>(() => {
  if (!promptState.selectedPrompt) return 'hidden';
  if (uiState.loading) return 'saving';
  if (lastSaveError.value) return 'error';
  if (hasUnsavedChanges.value) return 'dirty';
  // Touch the tick so `saved` transitions back to `idle` after the window elapses.
  void relativeTick.value;
  if (justSavedAt.value && Date.now() - justSavedAt.value < JUST_SAVED_WINDOW_MS) {
    return 'saved';
  }
  if (hasRealUpdatedAt(promptState.selectedPrompt.updatedAt)) return 'idle';
  return 'hidden';
});

// Clear the error state as soon as the user starts editing again.
watch(hasUnsavedChanges, (dirty) => {
  if (dirty && lastSaveError.value) {
    lastSaveError.value = false;
  }
});

// Computed properties for UISelect options
const promptOptions = computed(() => {
  const options: Array<{ value: string; label: string }> = [{ value: '', label: 'Select a prompt...' }];

  for (const [promptName] of groupedPrompts.value) {
    options.push({
      value: promptName,
      label: promptName,
    });
  }

  return options;
});

const versionOptions = computed(() => {
  const options: Array<{ value: string; label: string }> = [];

  for (const version of selectionState.availableVersions) {
    let label = formatVersion(version.version);
    if (startsWith(version.promptId, 'new-')) {
      label += ' (New)';
    }
    if (version.isActive) {
      label += ' (Active)';
    }

    options.push({
      value: version.version,
      label: label,
    });
  }

  return options;
});

const modelOptions = computed(() => {
  return availableModels.value.map((model) => ({
    value: model.arn,
    label: model.name,
  }));
});

// --------
// lifecycle
// ---------

onMounted(async () => {
  await loadPrompts();
  window.addEventListener('scroll', handleScroll);
  relativeTickInterval = setInterval(() => {
    relativeTick.value += 1;
  }, 30000);

  // Load available models
  try {
    availableModels.value = await aiService.getAllModels();
  } catch (e) {
    console.error('Failed to load models:', e);
  }

  // Reference data behind the list view's Policy Type / LOB column
  try {
    [promptMappings.value, coverageTypes.value] = await Promise.all([
      aiService.getPromptMappings(),
      referenceService.coverageTypes(),
    ]);
  } catch (e) {
    console.error('Failed to load prompt mappings:', e);
  }

  // Check for prompt query param and auto-select the prompt
  // Version is handled by the selectedPromptName watcher which checks route.query.version
  const promptParam = route.query.prompt as string;
  if (promptParam && groupedPrompts.value.has(promptParam)) {
    selectionState.selectedPromptName = promptParam;
  }
});

onUnmounted(() => {
  window.removeEventListener('scroll', handleScroll);
  if (relativeTickInterval) {
    clearInterval(relativeTickInterval);
    relativeTickInterval = null;
  }
});

// Watch for prompt selection changes
watch(
  () => promptState.selectedPrompt,
  (newPrompt) => {
    if (newPrompt) {
      // Always load fresh data from the prompt's original properties
      // This ensures switching between prompts always shows the correct content
      contentState.currentPromptContent = newPrompt.userPrompt || '';
      contentState.currentSystemText = newPrompt.systemPrompt || '';
      contentState.currentExpectedOutput = newPrompt.expectedOutputSchema || '';

      // Initialize current* properties if they don't exist
      // These are used to track edits, but we always prioritize the original data when switching
      if (newPrompt.currentPromptText === undefined) {
        newPrompt.currentPromptText = newPrompt.userPrompt || '';
      }
      if (newPrompt.currentSystemText === undefined) {
        newPrompt.currentSystemText = newPrompt.systemPrompt || '';
      }
      if (newPrompt.currentExpectedOutput === undefined) {
        newPrompt.currentExpectedOutput = newPrompt.expectedOutputSchema || '';
      }

      // Store original values for comparison
      formState.originalTitle = newPrompt.name || '';
      formState.originalDescription = newPrompt.description || '';

      // Sync entity-context flags from the server data
      entityContextState.carrier = newPrompt.requiresCarrierContext || false;
      entityContextState.coverageType = newPrompt.requiresCoverageTypeContext || false;
      entityContextState.wholesaler = newPrompt.requiresWholesalerContext || false;
      originalEntityContext.carrier = entityContextState.carrier;
      originalEntityContext.coverageType = entityContextState.coverageType;
      originalEntityContext.wholesaler = entityContextState.wholesaler;

      // Sync model selection
      selectedModelArn.value = newPrompt.modelArn || '';

      // Reset editing states when switching prompts
      uiState.editingTitle = false;
      uiState.editingDescription = false;
      formState.editedTitle = '';
      formState.editedDescription = '';

      // Update dropdown selections to match - use draft name for the prompt selection
      if (newPrompt.promptId) {
        // Find all prompts with the same ID
        const sameIdPrompts = promptState.prompts.filter((p) => p.promptId === newPrompt.promptId);
        const draftVersion = sameIdPrompts.find((p) => p.version.toLowerCase() === 'draft');
        const displayName = draftVersion?.name || newPrompt.name || '';

        if (selectionState.selectedPromptName !== displayName) {
          selectionState.selectedPromptName = displayName;
        }
        if (selectionState.selectedVersion !== newPrompt.version) {
          selectionState.selectedVersion = newPrompt.version;
        }
      }
    }
  },
);

// Watch for content changes
watch([() => contentState.currentPromptContent, () => contentState.currentSystemText, () => contentState.currentExpectedOutput], () => {
  if (promptState.selectedPrompt) {
    promptState.selectedPrompt.currentPromptText = contentState.currentPromptContent;
    promptState.selectedPrompt.currentSystemText = contentState.currentSystemText;
    promptState.selectedPrompt.currentExpectedOutput = contentState.currentExpectedOutput;
  }
});

// Clearing the schema clears the flags: server-side they live on the schema
// row and die with it.
watch(
  () => contentState.currentExpectedOutput,
  (newSchema) => {
    if (!newSchema) {
      entityContextState.carrier = false;
      entityContextState.coverageType = false;
      entityContextState.wholesaler = false;
    }
  },
);

// Watch for title and description changes
watch([() => formState.editedTitle, () => formState.editedDescription], () => {
  if (promptState.selectedPrompt) {
    // Apply changes immediately to the prompt object
    if (uiState.editingTitle && formState.editedTitle.trim() && !titleError.value) {
      promptState.selectedPrompt.name = formState.editedTitle.trim();
    }
    if (uiState.editingDescription) {
      promptState.selectedPrompt.description = formState.editedDescription.trim();
    }
  }
});

// Watch for prompt name selection
watch(
  () => selectionState.selectedPromptName,
  (newName) => {
    if (newName && groupedPrompts.value.has(newName)) {
      selectionState.availableVersions = groupedPrompts.value.get(newName)!;
      if (selectionState.availableVersions.length > 0) {
        // If the URL specifies this prompt with a specific version, respect it
        const urlPrompt = route.query.prompt as string;
        const urlVersion = route.query.version as string;
        if (urlPrompt === newName && urlVersion && selectionState.availableVersions.some((v) => v.version === urlVersion)) {
          selectionState.selectedVersion = urlVersion;
        } else {
          // Auto-select the first version (should be draft if available)
          selectionState.selectedVersion = selectionState.availableVersions[0].version;
        }
      }
    } else {
      selectionState.availableVersions = [];
      selectionState.selectedVersion = '';
    }
  },
);

// The list view loads prompts without their output schemas, so fetch the schema
// for a version at the point it is actually opened. Session-local prompts have
// no server-side row to fetch.
const hydratePromptSchema = async (prompt: Prompt | undefined) => {
  if (!prompt || prompt.schemaLoaded || startsWith(prompt.promptId, 'new-')) return;
  try {
    const full = await aiService.getPrompt(Number(prompt.promptId), prompt.version);
    prompt.expectedOutputSchema = full.expectedOutputSchema;
    prompt.requiresCarrierContext = full.requiresCarrierContext;
    prompt.requiresCoverageTypeContext = full.requiresCoverageTypeContext;
    prompt.requiresWholesalerContext = full.requiresWholesalerContext;
    prompt.schemaLoaded = true;
  } catch (e) {
    console.error('Failed to load prompt schema:', e);
  }
};

// Watch for BOTH prompt name AND version selection
// This ensures we detect changes even when switching between prompts with the same version (e.g., multiple drafts)
watch([() => selectionState.selectedPromptName, () => selectionState.selectedVersion], async ([newName, newVersion]) => {
  if (newName && newVersion) {
    const prompt = selectionState.availableVersions.find((p) => p.version === newVersion);
    if (prompt) {
      await hydratePromptSchema(prompt);
      promptState.selectedPrompt = prompt;
      // The publish diff compares against the latest published version's schema.
      await hydratePromptSchema(publishedPrompt.value);
    }
  }
});

// Watch for route query changes to handle prompt and version selection from URL
watch(
  () => [route.query.prompt, route.query.version],
  ([newPromptParam, newVersionParam]) => {
    if (newPromptParam && typeof newPromptParam === 'string' && groupedPrompts.value.has(newPromptParam)) {
      if (selectionState.selectedPromptName !== newPromptParam) {
        selectionState.selectedPromptName = newPromptParam;
      }
    }
    if (newVersionParam && typeof newVersionParam === 'string') {
      const matchingVersion = selectionState.availableVersions.find((v) => v.version === newVersionParam);
      if (matchingVersion && selectionState.selectedVersion !== newVersionParam) {
        selectionState.selectedVersion = newVersionParam;
      }
    }
  },
);

// Sync selection state to URL query params
watch([() => selectionState.selectedPromptName, () => selectionState.selectedVersion], ([newName, newVersion]) => {
  const query = { ...route.query };
  let changed = false;

  if (newName && query.prompt !== newName) {
    query.prompt = newName;
    changed = true;
  } else if (!newName && query.prompt) {
    delete query.prompt;
    changed = true;
  }

  if (newVersion && query.version !== newVersion) {
    query.version = newVersion;
    changed = true;
  } else if (!newVersion && query.version) {
    delete query.version;
    changed = true;
  }

  if (changed) {
    router.replace({ query });
  }
});

// ----------
// functions
// ----------

function startsWith(str: any, prefix: string) {
  return typeof str === 'string' && str.startsWith(prefix);
}

function isString(value: any): value is string {
  return typeof value === 'string';
}

const loadPrompts = async (updateLoadingMessage = true) => {
  if (updateLoadingMessage) {
    uiState.loading = true;
    uiState.loadingMessage = 'Loading prompts...';
  }
  try {
    promptState.prompts = await aiService.getAllPrompts(undefined, { includeSchema: false });
    promptState.prompts.unshift(...promptState.newPrompts); // Add any new prompts to the start of the list

    selectionState.availableVersions = groupedPrompts.value.get(selectionState.selectedPromptName) ?? [];
  } catch (e) {
    console.error('Failed to load prompts:', e);
  } finally {
    if (updateLoadingMessage) {
      uiState.loading = false;
    }
  }
};

const createNewSessionPrompt = async () => {
  const timestamp = Date.now();
  const newPrompt: Prompt = {
    promptVersionId: 0,
    promptId: `new-${timestamp}`,
    name: 'New-Prompt',
    version: 'DRAFT',
    description: '',
    designation: 'internal',
    userPrompt: '',
    systemPrompt: '',
    temperature: '1',
    maxTokens: 4000,
    isDraft: true,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
    deletedAt: '',
    isActive: false,
    currentPromptText: '',
  };

  const count = promptState.prompts.reduce((acc, prompt) => {
    if (prompt.name.includes('New-Prompt')) {
      acc++;
    }
    return acc;
  }, 0);

  if (count > 0) {
    newPrompt.name = `New-Prompt-${count}`;
  }

  promptState.prompts.unshift(newPrompt);
  promptState.newPrompts.unshift(newPrompt);

  // Select the newly created prompt using the dropdown structure
  // Since this is a new draft, use its name as the display name
  selectionState.selectedPromptName = newPrompt.name;
  selectionState.selectedVersion = newPrompt.version;
  promptState.selectedPrompt = newPrompt;

  // The editor is shown by the ?prompt= param, so creating from the list has to
  // move there rather than only setting the selection.
  await router.push({ query: { ...route.query, prompt: newPrompt.name, version: newPrompt.version } });

  // Use nextTick to ensure the prompt selection watcher has run first
  await nextTick();

  // Open title and description in edit mode for new prompts
  uiState.editingTitle = true;
  uiState.editingDescription = true;
  formState.editedTitle = newPrompt.name;
  formState.editedDescription = newPrompt.description;
};

// Helper functions for save operations.
// The three entity-context flags are sent unconditionally: the API treats an
// omitted flag as "preserve current", so a conditional spread would make it
// impossible to turn a flag off.
const createPromptRequest = (prompt: Prompt): CreatePromptRequest => ({
  name: prompt.name.trim(),
  description: (prompt.description || '').trim(),
  designation: prompt.designation || 'internal',
  userPrompt: contentState.currentPromptContent || prompt.userPrompt || '',
  ...((contentState.currentSystemText || prompt.systemPrompt || '').trim() && {
    systemPrompt: (contentState.currentSystemText || prompt.systemPrompt || '').trim(),
  }),
  ...(selectedModelArn.value && { modelArn: selectedModelArn.value }),
  ...(contentState.currentExpectedOutput && {
    expectedOutputSchema: contentState.currentExpectedOutput,
  }),
  requiresCarrierContext: entityContextState.carrier,
  requiresCoverageTypeContext: entityContextState.coverageType,
  requiresWholesalerContext: entityContextState.wholesaler,
});

const updatePromptRequest = (prompt: Prompt): UpdatePromptRequest => ({
  ...(formState.originalTitle !== formState.editedTitle && { name: prompt.name.trim() }),
  ...(formState.originalDescription !== formState.editedDescription && { description: (prompt.description || '').trim() }),
  userPrompt: contentState.currentPromptContent || prompt.userPrompt || '',
  ...((contentState.currentSystemText || prompt.systemPrompt || '').trim() && {
    systemPrompt: (contentState.currentSystemText || prompt.systemPrompt || '').trim(),
  }),
  modelArn: selectedModelArn.value,
  ...(contentState.currentExpectedOutput && {
    expectedOutputSchema: contentState.currentExpectedOutput,
  }),
  requiresCarrierContext: entityContextState.carrier,
  requiresCoverageTypeContext: entityContextState.coverageType,
  requiresWholesalerContext: entityContextState.wholesaler,
});

const handleSaveError = (error: any) => {
  console.error('Failed to save prompt draft:', error);

  if (error?.response?.data?.message) {
    Commit(StoreMutationType.SET_FLASH_MESSAGE, {
      text: error.response.data.message,
      color: 'error',
    });
  } else {
    Commit(StoreMutationType.SET_FLASH_MESSAGE, {
      text: 'Failed to save prompt draft. Please try again.',
      color: 'error',
    });
  }
};

const exitEditModes = () => {
  uiState.editingTitle = false;
  uiState.editingDescription = false;
  formState.editedTitle = '';
  formState.editedDescription = '';
};

const saveDraftOnly = async () => {
  const prompt = promptState.selectedPrompt;
  if (!prompt || !prompt.promptId || !prompt.name) return;

  exitEditModes();

  lastSaveError.value = false;

  try {
    uiState.loading = true;

    if (startsWith(prompt.promptId, 'new-')) {
      uiState.loadingMessage = 'Creating new prompt...';
      await saveNewPrompt(prompt);
    } else {
      uiState.loadingMessage = 'Updating draft...';
      await updateExistingPrompt(prompt);
    }
    justSavedAt.value = Date.now();
    // Schedule a tick after the "just saved" window so the status falls back to `idle`.
    setTimeout(() => {
      relativeTick.value += 1;
    }, JUST_SAVED_WINDOW_MS);
  } catch (error: any) {
    lastSaveError.value = true;
    handleSaveError(error);
  } finally {
    uiState.loading = false;
  }
};

const saveNewPrompt = async (prompt: Prompt) => {
  const request = createPromptRequest(prompt);
  const { promptId } = await aiService.createPrompt(request);

  // Remove from new prompts and refresh list
  promptState.newPrompts = promptState.newPrompts.filter((p) => p.promptId !== prompt.promptId);
  await loadPrompts(false); // Don't update loading message

  // Wait for computed properties (like groupedPrompts) to recalculate
  await nextTick();

  // Find and select the newly created prompt
  const createdPrompt = promptState.prompts.find((p) => p.promptId === promptId);
  if (createdPrompt) {
    // Update dropdown selections to match the newly created prompt
    const displayName = createdPrompt.name || '';
    await updateAvailableVersionsForPrompt(displayName);

    // Set the version and selected prompt
    selectionState.selectedVersion = createdPrompt.version;
    promptState.selectedPrompt = createdPrompt;

    // Update original values
    formState.originalTitle = createdPrompt.name || '';
    formState.originalDescription = createdPrompt.description || '';
  }
};

const updateExistingPrompt = async (prompt: Prompt) => {
  if (isString(prompt.promptId)) {
    console.error('invalid prompt id for existing prompt:', prompt.promptId);
    return;
  }

  const request = updatePromptRequest(prompt);
  await aiService.updatePromptDraftOnly(prompt.promptId, request);

  // Refresh the prompts list to get the latest data from server
  await loadPrompts(false); // Don't update loading message

  // Wait for computed properties (like groupedPrompts) to recalculate
  await nextTick();

  // Find and select the updated prompt from the refreshed list
  const updatedPrompt = promptState.prompts.find((p) => p.promptId === prompt.promptId);
  if (updatedPrompt) {
    // Update dropdown selections to match the updated prompt
    const displayName = updatedPrompt.name || '';
    await updateAvailableVersionsForPrompt(displayName);

    // Set the version and selected prompt
    selectionState.selectedVersion = updatedPrompt.version;
    promptState.selectedPrompt = updatedPrompt;

    // Update original values after successful save
    formState.originalTitle = updatedPrompt.name || '';
    formState.originalDescription = updatedPrompt.description || '';
  }
};

const publishPromptVersion = async (prompt: Prompt) => {
  if (!prompt.promptId || !prompt.name) return;

  if (!selectedModelArn.value) {
    modelFieldError.value = true;
    Commit(StoreMutationType.SET_FLASH_MESSAGE, {
      text: 'A model must be selected before publishing a version.',
      color: 'error',
    });
    return;
  }

  lastSaveError.value = false;

  try {
    uiState.loading = true;
    uiState.loadingMessage = 'Publishing version...';

    // First, save any unsaved changes to the draft
    if (hasUnsavedChanges.value) {
      if (startsWith(prompt.promptId, 'new-')) {
        uiState.loadingMessage = 'Creating prompt and publishing...';
        await saveNewPromptForPublish(prompt);
      } else {
        uiState.loadingMessage = 'Saving draft and publishing...';
        await saveExistingPromptForPublish(prompt);
      }
    }

    // Now publish the version
    uiState.loadingMessage = 'Publishing version...';
    await publishVersion(prompt);
    justSavedAt.value = Date.now();
    setTimeout(() => {
      relativeTick.value += 1;
    }, JUST_SAVED_WINDOW_MS);
  } catch (error: any) {
    lastSaveError.value = true;
    handlePublishError(error);
  } finally {
    uiState.loading = false;
  }
};

const saveNewPromptForPublish = async (prompt: Prompt) => {
  const request = createPromptRequest(prompt);
  await aiService.createPrompt(request);

  // Remove from new prompts
  promptState.newPrompts = promptState.newPrompts.filter((p) => p.promptId !== prompt.promptId);
};

const saveExistingPromptForPublish = async (prompt: Prompt) => {
  if (isString(prompt.promptId)) {
    console.error('save existing prompt failed: invalid prompt id for existing prompt:', prompt.promptId);
    return;
  }

  const request = updatePromptRequest(prompt);
  await aiService.updatePromptDraftOnly(prompt.promptId, request);
};

const publishVersion = async (prompt: Prompt) => {
  if (isString(prompt.promptId)) {
    console.error('publish version failed: invalid prompt id for existing prompt:', prompt.promptId);
    return;
  }

  const publishResponse = await aiService.publishPromptVersion(prompt.promptId);

  // Exit edit modes after successful publish
  exitEditModes();

  // Refresh the prompts list to get the latest data from server
  await loadPrompts(false); // Don't update loading message

  // Wait for computed properties (like groupedPrompts) to recalculate
  await nextTick();

  // After refreshing prompts, find the published version and its draft
  const publishedPrompt = promptState.prompts.find((p) => p.promptId === prompt.promptId && p.version === publishResponse.version);
  if (publishedPrompt) {
    await selectPublishedPrompt(publishedPrompt);
  }
};

const updateAvailableVersionsForPrompt = async (displayName: string) => {
  // Update the prompt name (may or may not trigger watcher if name unchanged)
  selectionState.selectedPromptName = displayName;

  // Wait for reactive updates
  await nextTick();

  // Manually update availableVersions from groupedPrompts to ensure it's current
  // (the watcher won't fire if the name didn't change)
  if (groupedPrompts.value.has(displayName)) {
    selectionState.availableVersions = groupedPrompts.value.get(displayName)!;
  }
};

const selectPublishedPrompt = async (publishedPrompt: Prompt) => {
  // Find all prompts with the same ID to get the draft
  const sameIdPrompts = promptState.prompts.filter((p) => p.promptId === publishedPrompt.promptId);
  const draftVersion = sameIdPrompts.find((p) => p.version.toLowerCase() === 'draft');
  const displayName = draftVersion?.name || publishedPrompt.name || '';

  // Update available versions to reflect any changes
  await updateAvailableVersionsForPrompt(displayName);

  // Wait for next tick to ensure all reactive updates have completed
  await nextTick();

  // Now we can safely update the version and prompt
  selectionState.selectedVersion = publishedPrompt.version;
  promptState.selectedPrompt = publishedPrompt;

  // Update original values after successful publish
  formState.originalTitle = publishedPrompt.name || '';
  formState.originalDescription = publishedPrompt.description || '';
};

const handlePublishError = (error: any) => {
  console.error('Failed to publish prompt version:', error);

  // handleResponse transforms axios errors into Error objects with cause property
  // The original axios response is stored in error.cause
  const errorData = error?.cause?.data;

  if (errorData?.code === 'MAX_VERSIONS_EXCEEDED') {
    Commit(StoreMutationType.SET_FLASH_MESSAGE, {
      text: `⚠️ ${errorData.error}: ${errorData.message}`,
      color: 'error',
    });
  } else if (errorData?.message) {
    Commit(StoreMutationType.SET_FLASH_MESSAGE, {
      text: errorData.message,
      color: 'error',
    });
  } else if (error?.message) {
    Commit(StoreMutationType.SET_FLASH_MESSAGE, {
      text: error.message,
      color: 'error',
    });
  } else {
    Commit(StoreMutationType.SET_FLASH_MESSAGE, {
      text: 'Failed to publish prompt version. Please try again.',
      color: 'error',
    });
  }
};

const deletePrompt = async (prompt: Prompt) => {
  if (!prompt.promptId || !prompt.version) {
    console.error('Cannot delete prompt: missing ID or Version');
    return;
  }

  uiState.loading = true;
  try {
    if (startsWith(prompt.promptId, 'new-')) {
      uiState.loadingMessage = 'Removing new prompt...';
      await deleteNewPrompt(prompt);
    } else if (prompt.version.toLowerCase() === 'draft') {
      uiState.loadingMessage = 'Deleting prompt and all versions...';
      await deleteDraftPrompt(prompt);
    } else {
      uiState.loadingMessage = 'Deleting prompt version...';
      await deleteVersionedPrompt(prompt);
    }
  } catch (error) {
    console.error('Failed to delete prompt:', error);
  } finally {
    uiState.loading = false;
  }
};

const deleteNewPrompt = async (prompt: Prompt) => {
  // for new prompts, just remove it from the list
  promptState.prompts = promptState.prompts.filter((p) => p.promptId !== prompt.promptId);
  promptState.newPrompts = promptState.newPrompts.filter((p) => p.promptId !== prompt.promptId);
  promptState.selectedPrompt = null;
  // Clear dropdown selections as well
  selectionState.selectedPromptName = '';
  selectionState.selectedVersion = '';
};

const deleteDraftPrompt = async (prompt: Prompt) => {
  if (isString(prompt.promptId)) {
    console.error('invalid prompt id for draft prompt:', prompt.promptId);
    return;
  }

  // for draft prompts, delete all versions of the prompt
  await aiService.deleteAllVersionsOfPrompt(prompt.promptId);

  // Refresh the prompts list to reflect the deletion
  await loadPrompts(false); // Don't update loading message

  // Clear the selected prompt since the entire draft (and all versions) was deleted
  promptState.selectedPrompt = null;
  selectionState.selectedPromptName = '';
  selectionState.selectedVersion = '';
};

const deleteVersionedPrompt = async (prompt: Prompt) => {
  if (isString(prompt.promptId)) {
    console.error('invalid prompt id for versioned prompt:', prompt.promptId);
    return;
  }

  // Store the prompt ID before deletion to find the draft version later
  const promptId = prompt.promptId;

  // for saved prompts, delete it from the server
  const versionNumber = parseInt(prompt.version, 10);
  await aiService.deletePrompt(promptId, versionNumber);

  // Refresh the prompts list to reflect the deletion
  await loadPrompts(false); // Don't update loading message

  // Wait for computed properties (like groupedPrompts) to recalculate
  await nextTick();

  // Try to switch back to the draft version of the same prompt
  const draftVersion = promptState.prompts.find((p) => p.promptId === promptId && p.version.toLowerCase() === 'draft');

  if (draftVersion) {
    const displayName = draftVersion.name || '';

    // Update available versions to reflect the deletion
    await updateAvailableVersionsForPrompt(displayName);

    // Switch to the draft version
    selectionState.selectedVersion = draftVersion.version;
    promptState.selectedPrompt = draftVersion;
  } else {
    // If no draft exists (shouldn't normally happen), clear selection
    promptState.selectedPrompt = null;
    selectionState.selectedPromptName = '';
    selectionState.selectedVersion = '';
  }
};

const handlePromptReset = (prompt: Prompt) => {
  contentState.currentPromptContent = prompt.userPrompt || '';
  contentState.currentSystemText = prompt.systemPrompt || '';
  contentState.currentExpectedOutput = prompt.expectedOutputSchema || '';
  selectedModelArn.value = prompt.modelArn || '';
  if (promptState.selectedPrompt) {
    promptState.selectedPrompt.currentPromptText = prompt.userPrompt || '';
    promptState.selectedPrompt.currentSystemText = prompt.systemPrompt || '';
    promptState.selectedPrompt.currentExpectedOutput = prompt.expectedOutputSchema || '';
    promptState.selectedPrompt.name = formState.originalTitle;
    promptState.selectedPrompt.description = formState.originalDescription;
  }

  // Exit editing modes and reset edited values
  uiState.editingTitle = false;
  uiState.editingDescription = false;
  formState.editedTitle = '';
  formState.editedDescription = '';
};

const handleDeletePrompt = () => {
  deleteModal.title = 'Confirm Delete Prompt';
  deleteModal.actionLabel = 'Delete';
  deleteModal.open = true;
};

const clearDeleteModal = () => {
  deleteModal.title = '';
  deleteModal.actionLabel = '';
  deleteModal.open = false;
};

const handleModalCancel = () => {
  clearDeleteModal();
};

const handleModalConfirm = async () => {
  if (!promptState.selectedPrompt) return;

  const prompt = promptState.selectedPrompt;

  // Call the original delete logic
  await deletePrompt(prompt);

  // Clear the modal and close
  clearDeleteModal();
};

function formatVersion(version: string) {
  if (version.toLowerCase() === 'draft') {
    return 'DRAFT';
  } else {
    return `v${version}`;
  }
}

const startEditingTitle = () => {
  if (promptState.selectedPrompt) {
    formState.editedTitle = promptState.selectedPrompt.name || '';
    uiState.editingTitle = true;
  }
};

const cancelEditingTitle = () => {
  if (promptState.selectedPrompt) {
    promptState.selectedPrompt.name = formState.originalTitle;
  }
  uiState.editingTitle = false;
  formState.editedTitle = '';
};

const startEditingDescription = () => {
  if (promptState.selectedPrompt) {
    formState.editedDescription = promptState.selectedPrompt.description || '';
    uiState.editingDescription = true;
  }
};

const cancelEditingDescription = () => {
  if (promptState.selectedPrompt) {
    promptState.selectedPrompt.description = formState.originalDescription;
  }
  uiState.editingDescription = false;
  formState.editedDescription = '';
};

// Scroll navigation functions
const handleScroll = () => {
  const scrollThreshold = 200; // Show button after scrolling 200px
  showScrollToTop.value = window.scrollY > scrollThreshold;
};

const scrollToTop = () => {
  window.scrollTo({ top: 0, behavior: 'smooth' });
};
</script>

<template>
  <div class="flex h-full flex-col gap-4">
    <!-- Header -->
    <div class="flex shrink-0 flex-wrap items-center justify-between gap-4">
      <div class="flex items-center gap-3">
        <h1 class="pl-2 text-2xl">Prompts</h1>
        <Button
          v-if="showList"
          size="sm"
          variant="outline"
          class="gap-1"
          :disabled="uiState.loading"
          @click="createNewSessionPrompt">
          <Plus class="h-4 w-4" />
          New Prompt
        </Button>
      </div>

      <div
        v-if="showList"
        class="flex items-center gap-3">
        <div class="w-56">
          <AppSelect
            v-model="listCoverageFilter"
            :options="coverageFilterOptions"
            class="h-8 w-full"
            placeholder="All coverage types"
            clearable
            aria-label="Filter by coverage type" />
        </div>

        <label class="search-bar input input-sm flex min-w-80 items-center gap-2">
          <input
            v-model="listSearch"
            type="text"
            placeholder="Search prompts by name"
            class="grow"
            autocomplete="off"
            aria-label="Search prompts by name" />
          <X
            v-if="listSearch"
            :size="18"
            class="cursor-pointer"
            @click="listSearch = ''" />
          <SearchIcon :size="18" />
        </label>
      </div>
    </div>

    <!-- List View -->
    <div
      v-if="showList"
      class="shrink-0">
      <Card class="border-slate-200 bg-white/80 backdrop-blur-sm">
        <CardContent class="p-4">
          <PromptListTable
            :rows="promptListRows"
            :loading="uiState.loading"
            :search-text="listSearch"
            :coverage-filter="listCoverageFilter"
            @select="openPromptFromList" />
        </CardContent>
      </Card>
    </div>

    <!-- Prompt Selection Bar -->
    <div
      v-if="!showList"
      class="shrink-0">
      <Card class="relative border-slate-200 bg-white/80 backdrop-blur-sm">
        <!-- Loading Overlay for Prompt Selection -->
        <div
          v-if="uiState.loading && promptState.prompts.length === 0"
          class="absolute inset-0 z-20 flex items-center justify-center rounded-lg bg-white/70 backdrop-blur-sm">
          <div class="flex flex-col items-center gap-1">
            <div class="h-8 w-8 animate-spin rounded-full border-4 border-slate-200 border-t-blue-500"></div>
            <p class="text-sm font-medium text-slate-600">{{ uiState.loadingMessage }}</p>
          </div>
        </div>

        <CardContent class="p-4">
          <div class="flex flex-wrap items-center gap-4">
            <Button
              size="sm"
              variant="outline"
              class="gap-1"
              @click="backToList">
              <ArrowLeft class="h-4 w-4" />
              All Prompts
            </Button>

            <!-- Prompt Dropdown -->
            <div class="flex min-w-[200px] flex-1 items-center gap-2">
              <label class="text-sm font-medium whitespace-nowrap text-slate-700">Prompt:</label>
              <div class="relative flex-1">
                <UISelect
                  v-model="selectionState.selectedPromptName"
                  :options="promptOptions"
                  :disabled="uiState.loading"
                  placeholder="Select a prompt..." />
              </div>
            </div>

            <!-- Version Dropdown -->
            <div
              v-if="selectionState.selectedPromptName"
              class="flex min-w-[120px] items-center gap-2">
              <label class="text-sm font-medium whitespace-nowrap text-slate-700">Version:</label>
              <div class="relative">
                <UISelect
                  :key="`version-select-${selectionState.selectedPromptName}-${selectionState.availableVersions.length}`"
                  v-model="selectionState.selectedVersion"
                  :options="versionOptions"
                  :disabled="uiState.loading"
                  placeholder="Select version..." />
              </div>
            </div>

            <!-- Action Buttons -->
            <div class="ml-auto flex items-center gap-2">
              <Button
                size="sm"
                variant="outline"
                class="gap-1"
                :disabled="uiState.loading"
                @click="createNewSessionPrompt">
                <Plus class="h-4 w-4" />
                New Prompt
              </Button>

              <button
                v-if="promptState.selectedPrompt"
                class="group btn border border-solid border-gray-400 btn-outline btn-sm hover:border-0 hover:bg-red-500"
                :disabled="uiState.loading"
                @click="handleDeletePrompt()">
                <Trash
                  :size="16"
                  class="text-red-500 group-hover:text-white" />
              </button>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>

    <!-- Full Width Prompt Editor -->
    <div
      v-if="!showList"
      class="flex min-h-0 flex-1">
      <Card
        v-if="promptState.selectedPrompt"
        class="relative flex w-full flex-col border-slate-200 bg-white/80 backdrop-blur-sm">
        <!-- Loading Overlay -->
        <div
          v-if="uiState.loading"
          class="absolute inset-0 z-20 flex items-center justify-center rounded-lg bg-white/90 backdrop-blur-sm">
          <div class="flex flex-col items-center gap-3">
            <div class="h-8 w-8 animate-spin rounded-full border-4 border-slate-200 border-t-blue-500"></div>
            <p class="text-sm font-medium text-slate-600">{{ uiState.loadingMessage }}</p>
          </div>
        </div>

        <CardHeader class="shrink-0">
          <CardTitle class="flex items-center justify-between text-lg">
            <div class="flex items-center gap-2">
              <Edit class="h-5 w-5 text-blue-600" />
              Edit Prompt
            </div>

            <div class="flex place-items-end items-center space-x-3">
              <SavedStatus
                :status="savedStatus"
                :updated-at="promptState.selectedPrompt?.updatedAt"
                :tick="relativeTick" />

              <!-- Validation error indicator -->
              <span
                v-if="outputFormatErrorCount > 0"
                class="inline-flex items-center gap-1 rounded-full bg-red-50 px-2.5 py-0.5 text-xs font-medium text-red-700 ring-1 ring-red-200 ring-inset"
                :title="`${outputFormatErrorCount} validation ${outputFormatErrorCount === 1 ? 'error' : 'errors'} in Output Format — fix before publishing`">
                {{ outputFormatErrorCount }} {{ outputFormatErrorCount === 1 ? 'error' : 'errors' }}
              </span>

              <!-- View changes button - shows diff between draft and active published version -->
              <Button
                v-if="!isReadonly && !isNewPrompt && hasChangesVsPublished"
                size="sm"
                variant="outline"
                class="gap-1"
                :disabled="uiState.loading"
                @click="showDiffModal = true">
                <GitCompare class="h-4 w-4" />
                View changes
              </Button>

              <!-- Publish Version button - always visible for draft prompts -->
              <Button
                v-if="!isReadonly && !isNewPrompt"
                size="sm"
                variant="default"
                @click="publishPromptVersion(promptState.selectedPrompt)"
                :disabled="uiState.loading || outputFormatErrorCount > 0"
                :title="outputFormatErrorCount > 0 ? 'Resolve validation errors before publishing' : ''"
                class="bg-blue-600 hover:bg-blue-700">
                Publish Version
              </Button>

              <!-- Save Draft button - only show when there are unsaved changes -->
              <Button
                v-if="hasUnsavedChanges && !isReadonly"
                size="sm"
                variant="outline"
                @click="saveDraftOnly()"
                :disabled="uiState.loading"
                class="">
                Save Draft
              </Button>

              <!-- Undo button -->
              <Button
                v-if="hasUnsavedChanges && !isReadonly"
                size="sm"
                @click="handlePromptReset(promptState.selectedPrompt)"
                :disabled="uiState.loading"
                class="text-gray-100 transition-colors hover:text-white"
                title="Undo change">
                <Undo2 class="h-4 w-4" />
              </Button>
            </div>
          </CardTitle>

          <div class="flex min-h-0 w-full flex-wrap gap-x-16 gap-y-4">
            <!-- Title Section -->
            <div class="shrink-0 space-y-2">
              <div class="flex items-center gap-2">
                <h4 class="text-sm font-medium text-slate-700">Title</h4>
                <button
                  v-if="!uiState.editingTitle && !isReadonly"
                  @click="startEditingTitle"
                  class="text-slate-400 hover:text-slate-600"
                  title="Edit title">
                  <Edit class="h-3 w-3" />
                </button>
              </div>
              <div
                v-if="uiState.editingTitle"
                class="space-y-2">
                <div class="flex items-center gap-2">
                  <Input
                    v-model="formState.editedTitle"
                    :class="`flex-1 ${titleError ? 'border-red-500 focus:border-red-500 focus:ring-red-500' : ''}`"
                    placeholder="Enter prompt title"
                    @keyup.escape="cancelEditingTitle" />
                  <button
                    @click="cancelEditingTitle"
                    class="text-red-600 hover:text-red-700"
                    title="Cancel">
                    <X class="h-4 w-4" />
                  </button>
                </div>
                <p
                  v-if="titleError"
                  class="text-sm text-red-600">
                  {{ titleError }}
                </p>
              </div>
              <div
                v-else
                class="flex min-h-10 items-center text-sm text-slate-900">
                {{ promptState.selectedPrompt.name || 'Untitled Prompt' }}
              </div>
            </div>

            <!-- Description Section -->
            <div class="max-w-2xl min-w-0 space-y-2">
              <div class="flex items-center gap-2">
                <h4 class="text-sm font-medium text-slate-700">Description</h4>
                <button
                  v-if="!uiState.editingDescription && !isReadonly"
                  @click="startEditingDescription"
                  class="text-slate-400 hover:text-slate-600"
                  title="Edit description">
                  <Edit class="h-3 w-3" />
                </button>
              </div>
              <div
                v-if="uiState.editingDescription"
                class="flex items-start gap-2">
                <Textarea
                  v-model="formState.editedDescription"
                  class="min-h-[60px] flex-1"
                  placeholder="Enter prompt description"
                  @keyup.escape="cancelEditingDescription" />
                <button
                  @click="cancelEditingDescription"
                  class="text-red-600 hover:text-red-700"
                  title="Cancel">
                  <X class="h-4 w-4" />
                </button>
              </div>
              <div
                v-else
                class="flex min-h-10 items-center text-sm text-slate-600">
                {{ promptState.selectedPrompt.description || 'No description provided' }}
              </div>
            </div>

            <!-- Designation Section -->
            <div class="shrink-0 space-y-2">
              <h4 class="text-sm font-medium text-slate-700">Designation</h4>
              <div
                v-if="isNewPrompt"
                class="space-y-1">
                <RadioGroup
                  :model-value="promptState.selectedPrompt.designation || 'internal'"
                  :options="designationOptions"
                  layout="horizontal"
                  :disabled="uiState.loading"
                  @update:model-value="promptState.selectedPrompt.designation = $event as PromptDesignation" />
                <p class="text-xs text-slate-500">Cannot be changed after the prompt is saved.</p>
              </div>
              <div
                v-else
                class="flex min-h-10 items-center text-sm text-slate-600">
                <Badge
                  v-if="promptState.selectedPrompt.designation === 'external'"
                  variant="destructive"
                  >External</Badge
                >
                <span v-else>Internal</span>
              </div>
            </div>

            <!-- Model Section -->
            <div class="shrink-0 space-y-2">
              <h4 class="text-sm font-medium text-slate-700">Model</h4>
              <div
                v-if="isReadonly"
                class="flex min-h-10 items-center text-sm text-slate-600">
                {{
                  availableModels.find((m) => m.arn === promptState.selectedPrompt?.modelArn)?.name ||
                  promptState.selectedPrompt?.modelArn ||
                  'No model selected'
                }}
              </div>
              <div
                v-else
                class="rounded-md"
                :class="modelFieldError ? 'ring-2 ring-red-500 ring-offset-2' : ''"
                @click="modelFieldError = false">
                <UISelect
                  v-model="selectedModelArn"
                  :options="modelOptions"
                  :disabled="uiState.loading"
                  placeholder="Select a model..." />
              </div>
            </div>
          </div>
        </CardHeader>
        <CardContent class="flex min-h-[30vh] flex-1 flex-col">
          <!-- Tabs for different sections -->
          <TabsRoot
            v-model="uiState.currentTab"
            class="flex min-h-0 flex-1 flex-col">
            <TabsList class="inline-flex h-10 w-fit items-center rounded-md bg-slate-100 p-1 text-slate-600">
              <TabsTrigger
                value="output-format"
                class="inline-flex items-center justify-center gap-2 rounded-sm px-3 py-1.5 text-sm font-medium whitespace-nowrap ring-offset-white transition-all focus-visible:ring-2 focus-visible:ring-slate-950 focus-visible:ring-offset-2 focus-visible:outline-none disabled:pointer-events-none disabled:opacity-50 data-[state=active]:bg-white data-[state=active]:text-slate-950 data-[state=active]:shadow-xs">
                Output Format
                <span
                  v-if="hasOutputFormatChanges"
                  class="relative flex h-2 w-2"
                  title="Unsaved changes">
                  <span class="absolute inline-flex h-full w-full animate-pulse rounded-full bg-amber-400 opacity-30"></span>
                  <span class="relative inline-flex h-2 w-2 rounded-full bg-amber-500"></span>
                </span>
              </TabsTrigger>
              <TabsTrigger
                value="system-instructions"
                class="inline-flex items-center justify-center gap-2 rounded-sm px-3 py-1.5 text-sm font-medium whitespace-nowrap ring-offset-white transition-all focus-visible:ring-2 focus-visible:ring-slate-950 focus-visible:ring-offset-2 focus-visible:outline-none disabled:pointer-events-none disabled:opacity-50 data-[state=active]:bg-white data-[state=active]:text-slate-950 data-[state=active]:shadow-xs">
                System Instructions
                <span
                  v-if="hasSystemInstructionsChanges"
                  class="relative flex h-2 w-2"
                  title="Unsaved changes">
                  <span class="absolute inline-flex h-full w-full animate-pulse rounded-full bg-amber-400 opacity-30"></span>
                  <span class="relative inline-flex h-2 w-2 rounded-full bg-amber-500"></span>
                </span>
              </TabsTrigger>
              <TabsTrigger
                value="prompt-text"
                class="inline-flex items-center justify-center gap-2 rounded-sm px-3 py-1.5 text-sm font-medium whitespace-nowrap ring-offset-white transition-all focus-visible:ring-2 focus-visible:ring-slate-950 focus-visible:ring-offset-2 focus-visible:outline-none disabled:pointer-events-none disabled:opacity-50 data-[state=active]:bg-white data-[state=active]:text-slate-950 data-[state=active]:shadow-xs">
                Prompt Text
                <span
                  v-if="hasPromptTextChanges"
                  class="relative flex h-2 w-2"
                  title="Unsaved changes">
                  <span class="absolute inline-flex h-full w-full animate-pulse rounded-full bg-amber-400 opacity-30"></span>
                  <span class="relative inline-flex h-2 w-2 rounded-full bg-amber-500"></span>
                </span>
              </TabsTrigger>
            </TabsList>

            <!-- Output Format Tab Content -->
            <TabsContent
              value="output-format"
              class="mt-4 flex min-h-0 flex-1 flex-col">
              <PromptEntityContextPanel
                v-model:carrier="entityContextState.carrier"
                v-model:coverage-type="entityContextState.coverageType"
                v-model:wholesaler="entityContextState.wholesaler"
                :readonly="isReadonly"
                :schema-present="!!contentState.currentExpectedOutput" />
              <JsonSchemaBuilder
                :key="`schema-${promptState.selectedPrompt?.promptId}-${promptState.selectedPrompt?.version}`"
                v-model="contentState.currentExpectedOutput"
                :disabled="isReadonly"
                :readonly="isReadonly"
                @update:error-count="outputFormatErrorCount = $event"
                class="min-h-0 flex-1 overflow-auto" />
            </TabsContent>

            <!-- System Instructions Tab Content -->
            <TabsContent
              value="system-instructions"
              class="mt-4 flex flex-col">
              <div class="flex flex-col space-y-2">
                <p
                  v-if="isReadonly"
                  class="text-xs text-slate-500 italic">
                  Editing is only allowed for draft prompts
                </p>
                <Textarea
                  v-model="contentState.currentSystemText"
                  :readonly="isReadonly"
                  :PermanentBorder="true"
                  :autoResize="true"
                  placeholder="Enter system instructions for the AI (optional)..."
                  :class="`min-h-[200px] resize-none font-mono text-sm ${isReadonly ? 'cursor-not-allowed bg-slate-50' : ''}`" />
              </div>
            </TabsContent>

            <!-- Prompt Text Tab Content -->
            <TabsContent
              value="prompt-text"
              class="mt-4 flex flex-col">
              <div class="flex flex-col space-y-2">
                <p
                  v-if="isReadonly"
                  class="text-xs text-slate-500 italic">
                  Editing is only allowed for draft prompts
                </p>
                <Textarea
                  v-model="contentState.currentPromptContent"
                  :readonly="isReadonly"
                  :PermanentBorder="true"
                  :autoResize="true"
                  placeholder="Enter your prompt template..."
                  :class="`min-h-[200px] resize-none font-mono text-sm ${isReadonly ? 'cursor-not-allowed bg-slate-50' : ''}`" />
              </div>
            </TabsContent>
          </TabsRoot>
        </CardContent>
      </Card>

      <!-- Empty State -->
      <Card
        v-else
        class="flex h-[60vh] w-full flex-col items-center justify-center border-slate-200 bg-white/80 backdrop-blur-sm">
        <Brain class="mx-auto mb-4 h-12 w-12 text-slate-400" />
        <h3 class="mb-2 text-lg font-medium text-slate-900">Select a Prompt</h3>
        <p class="text-sm text-slate-500">Choose a prompt from the dropdown to view and edit its details</p>
      </Card>
    </div>

    <!-- Delete Confirmation Modal -->
    <AppModal
      v-if="deleteModal.open"
      :show="deleteModal.open"
      :handle-cancel="handleModalCancel"
      :handle-action="handleModalConfirm"
      :action-button-label="deleteModal.actionLabel"
      :loading="uiState.loading"
      :action-disabled="uiState.loading">
      <template #title>
        <h2 class="pb-3">{{ deleteModal.title }}</h2>
      </template>
      <template #content>
        <div
          v-if="promptState.selectedPrompt"
          class="space-y-4">
          <p class="text-gray-700">
            Are you sure you want to delete this prompt?
            <span
              v-if="promptState.selectedPrompt.version.toLowerCase() === 'draft' && !startsWith(promptState.selectedPrompt.promptId, 'new-')"
              class="mt-2 block font-medium text-red-600">
              This will delete all versions of this prompt.
            </span>
          </p>

          <div class="rounded-lg border bg-gray-50 p-4">
            <div class="space-y-3">
              <div>
                <label class="text-sm font-medium text-gray-600">Name:</label>
                <p class="font-medium text-gray-900">{{ promptState.selectedPrompt.name }}</p>
              </div>

              <div>
                <label class="text-sm font-medium text-gray-600">Description:</label>
                <p class="text-gray-700">
                  {{ promptState.selectedPrompt.description || 'No description provided' }}
                </p>
              </div>

              <div>
                <label class="text-sm font-medium text-gray-600">Version:</label>
                <p class="font-medium text-gray-900">{{ formatVersion(promptState.selectedPrompt.version) }}</p>
              </div>
            </div>
          </div>
        </div>

        <div
          v-else
          class="text-red-600">
          No selected prompt found!
        </div>
      </template>
    </AppModal>

    <!-- Diff modal: draft vs currently-published version -->
    <PublishDiffModal
      v-if="publishedPrompt"
      v-model:open="showDiffModal"
      :draft-prompt="{
        expectedOutputSchema: contentState.currentExpectedOutput,
        systemPrompt: contentState.currentSystemText,
        userPrompt: contentState.currentPromptContent,
      }"
      :published-prompt="publishedPrompt"
      :section-changed="{
        outputFormat: schemaDiffersFromPublished,
        system: systemDiffersFromPublished,
        userPrompt: userPromptDiffersFromPublished,
      }" />

    <!-- Scroll to Top Button -->
    <Transition
      enter-active-class="transition-all duration-300 ease-out"
      leave-active-class="transition-all duration-200 ease-in"
      enter-from-class="translate-x-12 opacity-0"
      leave-to-class="translate-x-12 opacity-0">
      <button
        v-if="showScrollToTop"
        @click="scrollToTop"
        class="group fixed right-8 bottom-8 z-50 flex h-12 w-12 items-center justify-center rounded-full bg-blue-600 text-white shadow-lg transition-all duration-200 hover:scale-110 hover:bg-blue-700 hover:shadow-xl focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 focus:outline-none"
        title="Jump to top"
        aria-label="Jump to top">
        <ArrowUp class="h-5 w-5 transition-transform group-hover:-translate-y-0.5" />
      </button>
    </Transition>
  </div>
</template>
